import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { marked } from "marked";
import DOMPurify from "dompurify";
import PublicShell from "@/components/PublicShell";
import { GoldDivider, LuxuryCard } from "@/components/luxury";
import { findPost, POSTS } from "@/content/journal";
import { ArrowLeftIcon as ArrowLeft, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import { Icon } from "@/components/luxury";
import ShareStrip from "@/components/ShareStrip";
import api from "@/lib/api";

const SITE_NAME = "Armstrong K9 Registry";

/**
 * Single journal post.
 *
 * Resolves the slug against two sources, in order:
 *   1. Static JSX posts in /content/journal — the curated editorials
 *      that ship custom JSX (WaxSeal, gold ornaments, etc.).
 *   2. DB posts written through /admin/journal — Markdown body that we
 *      render through `marked` + DOMPurify.
 *
 * If neither source has the slug we hard-redirect to /journal. The
 * "Continue reading" card at the bottom prefers whatever the newest
 * post is across both sources (excluding the current one).
 */
export default function JournalPost() {
  const { slug } = useParams();
  const staticPost = findPost(slug);
  const [dbPost, setDbPost] = useState(undefined); // undefined = loading
  const [dbAll, setDbAll] = useState([]);
  const [footerDisclaimer, setFooterDisclaimer] = useState("");

  // Try the CMS lookup. If staticPost already matched we still pull the
  // list (so "Continue reading" can pick from both worlds) but skip the
  // detail fetch.
  useEffect(() => {
    let active = true;
    if (!staticPost) {
      api
        .get(`/journal/posts/${slug}`)
        .then((r) => active && setDbPost(r.data || null))
        .catch(() => active && setDbPost(null));
    } else {
      setDbPost(null);
    }
    api
      .get("/journal/posts")
      .then((r) => active && setDbAll(Array.isArray(r.data?.posts) ? r.data.posts : []))
      .catch(() => active && setDbAll([]));
    api
      .get("/journal/settings")
      .then((r) => active && setFooterDisclaimer(r.data?.footer_disclaimer || ""))
      .catch(() => active && setFooterDisclaimer(""));
    return () => {
      active = false;
    };
  }, [slug, staticPost]);

  const post = staticPost || (dbPost || null);

  // Render Markdown safely for DB-backed posts. DOMPurify strips any
  // <script>, inline handlers, or other dangerous payloads before we
  // hand the HTML to React via dangerouslySetInnerHTML. Computed
  // unconditionally so the hook order stays stable across renders.
  const dbHtml = React.useMemo(() => {
    if (staticPost || !dbPost?.body_md) return "";
    try {
      return DOMPurify.sanitize(marked.parse(dbPost.body_md));
    } catch {
      return "";
    }
  }, [staticPost, dbPost]);

  useEffect(() => {
    if (post) {
      window.scrollTo({ top: 0, behavior: "instant" });
      document.title = `${post.title} · Armstrong K9 Journal`;
    }
    return () => {
      document.title = "Armstrong K9 Registry";
    };
  }, [post]);

  // Still waiting on the CMS lookup — render nothing rather than
  // briefly flashing a redirect to /journal.
  if (!staticPost && dbPost === undefined) {
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 text-[#6B6B6B]">Loading…</div>
      </PublicShell>
    );
  }

  if (!post) {
    return <Navigate to="/journal" replace />;
  }

  // Build the "continue reading" pool from both sources, exclude self,
  // newest first. Static posts use `publishedAt`, DB posts use
  // `published_at`/`updated_at`.
  const staticPool = POSTS.map((p) => ({
    slug: p.slug,
    title: p.title,
    dek: p.dek,
    publishedAt: p.publishedAt,
  }));
  const dbPool = (dbAll || []).map((p) => ({
    slug: p.slug,
    title: p.title,
    dek: p.dek,
    publishedAt: (p.published_at || p.updated_at || p.created_at || "").slice(0, 10),
  }));
  const continuePost =
    [...staticPool, ...dbPool]
      .filter((p) => p.slug !== (post.slug || slug))
      .sort((a, b) => (b.publishedAt || "").localeCompare(a.publishedAt || ""))[0] || null;

  const isStatic = !!staticPost;
  const Body = isStatic ? staticPost.Body : null;
  const publishedAtStr = isStatic
    ? staticPost.publishedAt
    : (dbPost?.published_at || dbPost?.updated_at || dbPost?.created_at || "");
  const publishedDate = publishedAtStr
    ? new Date(publishedAtStr).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";
  const readMinutes = isStatic ? staticPost.readMinutes : (dbPost?.read_minutes || 1);

  // SEO/Social wiring — title, description, canonical URL, OpenGraph,
  // Twitter Card, and JSON-LD `BlogPosting`. Keeps every post indexed
  // with rich previews in iMessage/WhatsApp/X/LinkedIn/Slack.
  const origin = typeof window !== "undefined" ? webOrigin() : "";
  const pageUrl = `${origin}/journal/${post.slug || slug}`;
  const pageTitle = `${post.title} · ${SITE_NAME} Journal`;
  const pageDesc = post.dek || `An editorial from the ${SITE_NAME} Journal.`;
  const ogImage =
    (!isStatic && dbPost?.cover_image_url) ? dbPost.cover_image_url : `${origin}/logo-512.png`;
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: pageDesc,
    image: ogImage,
    datePublished: publishedAtStr || undefined,
    dateModified: (!isStatic && dbPost?.updated_at) ? dbPost.updated_at : publishedAtStr || undefined,
    author: {
      "@type": isStatic ? "Organization" : "Person",
      name: isStatic ? SITE_NAME : (dbPost?.author_name || SITE_NAME),
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${origin}/logo-512.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
    articleSection: post.section,
  };

  const perPostDisclaimer = !isStatic ? (dbPost?.disclaimer || "") : "";

  return (
    <PublicShell>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImage} />
        {publishedAtStr && <meta property="article:published_time" content={publishedAtStr} />}
        {post.section && <meta property="article:section" content={post.section} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      </Helmet>
      <article className="max-w-3xl mx-auto px-6 md:px-10 pt-10 pb-20" data-testid={`journal-post-${post.slug || slug}`}>
        {/* Back to journal — small, unobtrusive */}
        <Link
          to="/journal"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-[#6B6B6B] hover:text-ink mb-8 transition-colors"
          data-testid="journal-back-link"
        >
          <Icon as={ArrowLeft} tone="current" size="xs" weight="bold" />
          Back to the journal
        </Link>

        {/* Article header */}
        <header className="mb-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-4">
            {post.section}
          </p>
          {!isStatic && dbPost?.cover_image_url && (
            <img
              src={dbPost.cover_image_url}
              alt=""
              className="rounded-2xl w-full h-72 object-cover mb-6 border border-[#EAEAEA]"
              data-testid="journal-post-cover"
            />
          )}
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            {post.title}
          </h1>
          {!isStatic && post.dek && (
            <p className="mt-5 text-[#3a3a3a] leading-relaxed max-w-2xl text-[15px]">
              {post.dek}
            </p>
          )}
          <div className="gold-hairline mt-7 mb-4 max-w-md" />
          <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.22em] font-bold text-[#6B6B6B] font-tabular">
            {publishedDate && <span>{publishedDate}</span>}
            {publishedDate && <span className="text-gold">·</span>}
            <span>{readMinutes}-min read</span>
            {!isStatic && dbPost?.author_name && (
              <>
                <span className="text-gold">·</span>
                <span className="normal-case tracking-normal font-medium text-[#6B6B6B]">By {dbPost.author_name}</span>
              </>
            )}
          </div>
          <div className="mt-6">
            <ShareStrip url={pageUrl} title={post.title} dek={pageDesc} testIdPrefix="journal-share-top" />
          </div>
        </header>

        {/* Optional per-post disclaimer — surfaces above the body in
            italic gray so legal-mandated framing is impossible to
            miss without breaking editorial rhythm. */}
        {perPostDisclaimer && (
          <aside
            className="mb-8 border-l-2 border-[#C9A227]/60 bg-[#FBF7EC]/60 px-4 py-3 text-[12.5px] italic text-[#3a3a3a] leading-relaxed"
            data-testid="journal-post-disclaimer"
          >
            {perPostDisclaimer}
          </aside>
        )}

        {/* Body — JSX for static posts, sanitized Markdown for CMS posts */}
        <div className="journal-prose" data-testid="journal-post-body">
          {isStatic ? <Body /> : (
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: dbHtml }}
            />
          )}
        </div>

        {/* Bottom share strip — encourages share after they've read */}
        <div className="mt-12 flex flex-col items-center gap-3">
          <ShareStrip url={pageUrl} title={post.title} dek={pageDesc} testIdPrefix="journal-share-bottom" />
        </div>

        {/* Continue reading */}
        {continuePost && (
          <>
            <GoldDivider variant="ornament" className="max-w-md mx-auto my-16" />
            <Link to={`/journal/${continuePost.slug}`} className="group block" data-testid="journal-continue-link">
              <LuxuryCard interactive className="p-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-2">
                  Continue reading
                </p>
                <h3 className="font-spectral text-2xl tracking-tight group-hover:text-crimson transition-colors">
                  {continuePost.title}
                </h3>
                <p className="text-sm text-[#6B6B6B] mt-2 leading-relaxed">
                  {continuePost.dek}
                </p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] font-bold text-ink group-hover:text-crimson transition-colors">
                  Read the piece
                  <Icon as={ArrowRight} tone="current" size="xs" weight="bold" />
                </div>
              </LuxuryCard>
            </Link>
          </>
        )}

        {/* Standing footer disclaimer — admin-editable from
            /admin/journal/settings. Renders on every post + the index. */}
        {footerDisclaimer && (
          <>
            <div className="gold-hairline mt-16 mb-4 max-w-md mx-auto" />
            <p
              className="text-[11px] leading-relaxed text-[#6B6B6B] italic max-w-2xl mx-auto text-center"
              data-testid="journal-footer-disclaimer"
            >
              {footerDisclaimer}
            </p>
          </>
        )}
      </article>
    </PublicShell>
  );
}
